import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Grid,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {DataGrid} from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DescriptionIcon from "@mui/icons-material/Description";
import EventIcon from "@mui/icons-material/Event";
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import {useNavigate, useParams} from "react-router-dom";
import {api} from "../../services/apiService";
import {formatDateOnlyISO} from "../../utils/helpers";
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import WarningIcon from '@mui/icons-material/Warning';

export default function ClientDetails() {
    const params = useParams();
    const navigate = useNavigate();
    const [tab, setTab] = useState(0);
    const [clientData, setClientData] = useState(null);
    const [reservations, setReservations] = useState([]);
    const [estimates, setEstimates] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(true);

    const handleChangeTab = (event, newValue) => {
        setTab(newValue);
    };

    useEffect(() => {
        const promises = [];

        promises.push(api.get("/clients/" + params.id)
            .then(response => {
                setClientData(response.data);
            })
            .catch(error => {
                console.error("Erreur lors de la récupération des détails du client:", error);
                setTimeout(() => {
                    navigate("/clients");
                }, 2000);
            }));

        promises.push(api.post("/bookings/list?page=" + page + "&per_page=" + pageSize + "&user_id=" + params.id, {
            filters: []
        })
            .then(response => {
                setReservations(() => {
                    const data = response.data;
                    return {
                        ...data,
                        bookings: data.bookings.map(booking => {
                            return {
                                ...booking,
                                hasGite: booking.productsCategories.some(p => p.toLowerCase().includes("gîte") || p.toLowerCase().includes("gite")),
                                hasSalle: booking.productsCategories.some(p => p.toLowerCase().includes("salle")),
                                hasChambres: booking.productsCategories.some(p => p.toLowerCase().includes("chambre")),
                            }
                        })
                    }
                });
            })
            .catch(error => {
                console.error("Erreur lors de la récupération des réservations:", error);
            }));

        promises.push(api.get(`/estimates/list?client=${params.id}&per_page=${pageSize}&page=${page}`)
            .then(res => {
                setEstimates(res.data.items);
            })
        );

        Promise.all(promises).finally(() => {
            setLoading(false);
        });
    }, [params.id, navigate, page, pageSize]);

    const bookingsColumns = [
        {field: "bookingRef", headerName: "N° Réservation", flex: 1, minWidth: 150},
        {
            field: "startTime",
            headerName: "Date prévue",
            flex: 1,
            minWidth: 120,
            valueFormatter: (params) => new Date(params).toLocaleDateString('fr-FR')
        },
        {
            field: "total",
            headerName: "Total TTC (€)",
            flex: 1,
            minWidth: 130,
            valueFormatter: (params) =>
                params.toLocaleString("fr-FR", {
                    style: "currency",
                    currency: "EUR",
                }),
        },
        {
            field: "status",
            headerName: "Statut",
            flex: 1,
            minWidth: 120,
            valueGetter: (params) => params.label
        },
        {
            field: "hasGite",
            headerName: "Gîte",
            flex: 0.7,
            minWidth: 80,
            renderCell: (params) =>
                params.value ? (
                    <CheckIcon sx={{height: "100%", color: "green"}}/>
                ) : (
                    <ClearIcon sx={{height: "100%", color: "red"}}/>
                )
        },
        {
            field: "hasSalle",
            headerName: "Salle",
            flex: 0.7,
            minWidth: 80,
            renderCell: (params) =>
                params.value ? (
                    <CheckIcon sx={{height: "100%", color: "green"}}/>
                ) : (
                    <ClearIcon sx={{height: "100%", color: "red"}}/>
                )
        },
        {
            field: "hasChambres",
            headerName: "Chambres",
            flex: 0.8,
            minWidth: 100,
            renderCell: (params) =>
                params.value ? (
                    <CheckIcon sx={{height: "100%", color: "green"}}/>
                ) : (
                    <ClearIcon sx={{height: "100%", color: "red"}}/>
                )
        }
    ];

    const factureColumns = [
        {
            field: "status",
            headerName: "",
            width: 5,
            renderCell: (params) => {
                const icon = params.value.icon;
                if (icon === "paid") {
                    return <Tooltip title={params.value.label}><CheckCircleIcon sx={{color: 'green', height: '100%'}}/></Tooltip>;
                } else if (icon === "waiting") {
                    return <Tooltip title={params.value.label}><HourglassTopIcon
                        style={{color: '#ffc864', height: '100%'}}/></Tooltip>;
                } else if (icon === "cancelled") {
                    return <Tooltip title={params.value.label}><CancelIcon
                        style={{color: 'red', height: '100%'}}/></Tooltip>;
                } else if (icon === "overdue") {
                    return <Tooltip title={params.value.label}><WarningIcon
                        style={{color: 'darkorange', height: '100%'}}/></Tooltip>;
                } else {
                    return null;
                }
            }
        },
        {field: "ref", headerName: "N° Facture", flex: 1, minWidth: 120},
        {
            field: "created_at",
            headerName: "Date de création",
            flex: 1,
            minWidth: 100,
            valueGetter: params => formatDateOnlyISO(params)
        },
        {
            field: "due_date",
            headerName: "Date d'échéance",
            flex: 1,
            minWidth: 100,
            valueGetter: params => formatDateOnlyISO(params)
        },
        {
            field: "total_ttc",
            headerName: "Montant TTC (€)",
            flex: 1,
            minWidth: 120,
            valueFormatter: (params) =>
                params.toLocaleString("fr-FR", {
                    style: "currency",
                    currency: "EUR",
                }),
        },
        {
            field: "type",
            headerName: "Type",
            minWidth: 80,
            valueFormatter: (params) => params === "deposit" ? "Acompte" : "Solde",
        }
    ];

    const devisColumns = [
        {field: "ref", headerName: "N° Devis", flex: 1, minWidth: 120},
        {field: "booking_ref", headerName: "N° Réservation", flex: 1, minWidth: 120},
        {
            field: "created_at",
            headerName: "Date de création",
            flex: 1,
            minWidth: 100,
            valueGetter: params => formatDateOnlyISO(params)
        },
        {
            field: "due_date",
            headerName: "Date d'expiration",
            flex: 1,
            minWidth: 120,
            valueGetter: params => formatDateOnlyISO(params)
        },
        {
            field: "total_ttc",
            headerName: "Montant TTC (€)",
            flex: 1,
            minWidth: 120,
            valueFormatter: (params) =>
                params.toLocaleString("fr-FR", {
                    style: "currency",
                    currency: "EUR",
                }),
        }
    ];


    return (
        !loading ? (
                <Container maxWidth="lg" sx={{mt: {xs: 2, sm: 4}}}>
                    {/* Informations principales */}
                    <Paper sx={{p: 3, mb: 4}}>
                        <Grid container spacing={3} justifyContent={"space-between"}>
                            <Grid>
                                <Box sx={{
                                    display: "flex",
                                    flexDirection: {xs: "column", sm: "row"},
                                    alignItems: {xs: "flex-start", sm: "center"},
                                    gap: 1,
                                    mb: 1
                                }}>
                                    <Typography variant="h5">
                                        {clientData.name}
                                    </Typography>
                                    <Chip
                                        label={clientData.isCompany ? "Professionnel" : "Particulier"}
                                        color="primary"
                                        size="small"
                                    />
                                </Box>
                                <Typography variant="body1">{clientData.phone}</Typography>
                                <Typography variant="body1">{clientData.email}</Typography>
                                <Typography variant="body1">
                                    {clientData.address && `${clientData.address}, `}
                                    {clientData.postalCode && `${clientData.postalCode} `}
                                    {clientData.city && clientData.city}
                                </Typography>
                            </Grid>
                            <Grid sx={{width: {xs: "100%", sm: "auto"}}}>
                                <Stack spacing={2}>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon/>}
                                        color="primary"
                                        sx={{width: {xs: "100%", sm: "auto"}}}
                                        onClick={() => navigate("/bookings/create?clientId=" + params.id)}
                                    >
                                        Nouvelle réservation
                                    </Button>
                                    <Button variant="outlined" startIcon={<EditIcon/>}
                                            sx={{width: {xs: "100%", sm: "auto"}}}
                                            onClick={() => navigate("/clients/edit/" + params.id)}>
                                        Modifier profil
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Historique */}
                    <Paper sx={{p: 2, mb: 4}}>
                        <Tabs value={tab} onChange={handleChangeTab} sx={{mb: 2}} scrollButtons="auto"
                              allowScrollButtonsMobile variant="scrollable">
                            <Tab label="Réservations" icon={<EventIcon/>} iconPosition="start"/>
                            <Tab
                                label="Devis"
                                icon={<DescriptionIcon/>}
                                iconPosition="start"
                            />
                        </Tabs>
                        {tab === 0 && (
                            <DataGrid
                                rows={reservations.bookings}
                                columns={bookingsColumns}
                                pageSize={5}
                                rowsPerPageOptions={[5, 10, 20]}
                                pagination
                                onPageChange={(newPage) => setPage(newPage + 1)}
                                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                                paginationMode="server"
                                rowCount={reservations.total || 0}
                                onRowClick={(params) => navigate(`/bookings/details/${params.id}`)}
                                sx={{
                                    cursor: "pointer",
                                    "& .MuiDataGrid-row:hover": {
                                        backgroundColor: "#f0f0f0",
                                    },
                                    backgroundColor: "#fff"
                                }}
                            />
                        )}
                        {tab === 1 && (
                            <DataGrid
                                rows={estimates}
                                columns={devisColumns}
                                autoHeight
                                pageSize={5}
                                sx={{
                                    cursor: "pointer",
                                    "& .MuiDataGrid-row:hover": {
                                        backgroundColor: "#f0f0f0",
                                    },
                                    backgroundColor: "#fff"
                                }}
                            />
                        )}
                    </Paper>

                    {/* Notes internes */}
                    <Paper sx={{p: 3}}>
                        <Typography variant="h6" gutterBottom>
                            Notes internes
                        </Typography>
                        <TextField
                            multiline
                            rows={4}
                            fullWidth
                            placeholder="Ajouter une note interne sur ce client..."
                        />
                    </Paper>
                </Container>
            )
            :
            (
                <Container maxWidth="lg" sx={{
                    mt: 4,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "60vh"
                }}>
                    <Stack spacing={2} alignItems="center">
                        <Box>
                            <Typography variant="h6" sx={{mb: 2}}>Chargement des données client...</Typography>
                            <Box sx={{display: "flex", justifyContent: "center"}}>
                                <CircularProgress color="primary"/>
                            </Box>
                        </Box>
                    </Stack>
                </Container>
            )
    );
}
